/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { useAppSelector } from '@ibm-watson/shared/store';
import { useChannelHelpers } from '../../../hooks/useChannelHelpers';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  didSubscribeIntegrationSucceed,
  selectDraftIntegration
} from '../../../store/channels/selectors';
import { QuickSetupComponentProps } from '../../../util/types';
import { FIELDS } from '../schema';
import { ConfigureGenesysBotConnectorContent } from './ConfigureGenesysBotConnectorContent';
import { ConfigureGenesysBotConnectorStepMessages as messages } from './messages';

export const ConfigureGenesysBotConnectorStep = ({
  assistantId,
  environmentId,
  markStepComplete
}: QuickSetupComponentProps) => {
  const formMethods = useFormContext();
  const { subscribeForWebhookUrl } = useChannelHelpers(formMethods);

  const draftIntegration = useAppSelector(selectDraftIntegration, shallowEqual);
  const subscribeSucceeded = useAppSelector(didSubscribeIntegrationSucceed);

  const [subscribeCalled, setSubscribeCalled] = useState<boolean>(false);

  const { getFieldValue } = useFormHelpers(formMethods);

  useEffect(() => {
    markStepComplete(subscribeSucceeded);
  }, [markStepComplete, subscribeSucceeded]);

  const subscribeIntegration = useCallback(
    () =>
      subscribeForWebhookUrl(
        assistantId,
        environmentId,
        draftIntegration,
        {
          client_id: getFieldValue(FIELDS.CLIENT_ID),
          client_secret: getFieldValue(FIELDS.CLIENT_SECRET),
          verification_token: getFieldValue(FIELDS.VERIFICATION_TOKEN),
          bot_connector_id: getFieldValue(FIELDS.BOT_CONNECTOR_ID),
          api_url: getFieldValue(FIELDS.API_URL)
        },
        FIELDS.WEBHOOK_URL
      ),
    [
      assistantId,
      draftIntegration,
      environmentId,
      getFieldValue,
      subscribeForWebhookUrl
    ]
  );

  /**
   * Call webhook subscribe
   */
  useEffect(() => {
    if (!subscribeCalled && !_.isNil(assistantId) && !_.isNil(environmentId)) {
      subscribeIntegration();
      setSubscribeCalled(true);
    }
  }, [
    assistantId,
    environmentId,
    subscribeCalled,
    subscribeForWebhookUrl,
    subscribeIntegration
  ]);

  return (
    <div className="TabContent__body-content TabContent__body-outdent">
      <div className="QuickSetup__step-title">
        <FormattedMessage {...messages.title} />
      </div>
      <div className="TabContent__spacer8" />
      <ConfigureGenesysBotConnectorContent
        subscribeIntegration={subscribeIntegration}
        isQuickSetup
      />
    </div>
  );
};
